import { Component, Fragment, type ReactNode, useEffect } from 'react'
import { Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom'
import { Layout } from './components/Layout'
import { GATEWAYS, LOCALES, LocaleProvider, localeMeta } from './i18n'
import { AuthProvider } from './lib/auth'
import {
  checkForNewVersion,
  isChunkLoadError,
  isNewVersionAvailable,
  reloadForStaleChunk,
} from './lib/stale-chunk'
import { AccountPage } from './pages/AccountPage'
import { AdvertisePage } from './pages/AdvertisePage'
import { CnPartnersPage } from './pages/CnPartnersPage'
import { CountryLandingPage } from './pages/CountryLandingPage'
import { DashboardPage } from './pages/DashboardPage'
import { DevelopersPage } from './pages/DevelopersPage'
import { HomePage } from './pages/HomePage'
import { IndexPage } from './pages/IndexPage'
import { LanePage } from './pages/LanePage'
import { LanesHubPage } from './pages/LanesHubPage'
import { McpPage } from './pages/McpPage'
import { PricingPage } from './pages/PricingPage'
import { RfqPage } from './pages/RfqPage'
import { TradePage } from './pages/TradePage'

// The shared route set, emitted once per locale. English uses the '' prefix (site root); every other
// locale is served from its subdirectory (/de, …). Keep in sync with prerender.mjs + worker.js.
function localeRoutes(prefix: string) {
  const p = (s: string) => `${prefix}${s}`
  return (
    <Fragment key={prefix || 'root'}>
      <Route path={prefix || '/'} element={<HomePage />} />
      <Route path={p('/rate-index')} element={<IndexPage />} />
      <Route path={p('/rates')} element={<LanesHubPage />} />
      <Route path={p('/rates/:slug')} element={<LanePage />} />
      <Route path={p('/trade/:slug')} element={<TradePage />} />
      <Route path={p('/book')} element={<RfqPage />} />
      <Route path={p('/advertise')} element={<AdvertisePage />} />
      <Route path={p('/developers')} element={<DevelopersPage />} />
      <Route path={p('/pricing')} element={<PricingPage />} />
      <Route path={p('/account')} element={<AccountPage />} />
    </Fragment>
  )
}

// Stale-chunk PREVENTION: poll /version.json; when a newer deploy is live, reload at the
// NEXT route change — a graceful moment that pre-empts a chunk 404 instead of recovering
// after one. Poll on mount + every 5 min + when the tab regains focus.
function VersionGuard() {
  const { pathname } = useLocation()

  useEffect(() => {
    void checkForNewVersion()
    const id = window.setInterval(() => void checkForNewVersion(), 5 * 60_000)
    const onFocus = () => void checkForNewVersion()
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  useEffect(() => {
    if (isNewVersionAvailable()) reloadForStaleChunk()
  }, [pathname])

  return null
}

// Catches a render throw from ANY page so one bad component (or a bad build) degrades to a reload
// prompt instead of a white screen. Chunk-load errors self-heal via reloadForStaleChunk(); everything
// else shows a message + Reload. Wraps ONLY <Routes> — the Layout chrome stays alive and the visitor
// can navigate away, which clears the error (componentDidUpdate on pathname change) without a reload.
class RouteErrorBoundaryInner extends Component<
  { pathname: string; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }
  componentDidCatch(error: unknown) {
    if (isChunkLoadError(error) || isNewVersionAvailable()) reloadForStaleChunk()
  }
  componentDidUpdate(prev: { pathname: string }) {
    if (this.state.hasError && prev.pathname !== this.props.pathname) {
      this.setState({ hasError: false })
    }
  }
  render() {
    if (!this.state.hasError) return this.props.children
    return (
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '96px 24px', textAlign: 'center' }}>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: 'var(--text-primary, #0b0c0e)',
            margin: '0 0 12px',
          }}
        >
          Something went wrong on this page
        </h1>
        <p
          style={{
            fontSize: 15,
            color: 'var(--text-secondary, #5c6672)',
            lineHeight: 1.6,
            margin: '0 0 24px',
          }}
        >
          A temporary glitch — reloading usually fixes it. If it keeps happening, let us know.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--on-accent, #fff)',
            background: 'var(--signal, #1f4fe6)',
            border: 'none',
            borderRadius: 10,
            padding: '11px 24px',
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    )
  }
}

function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  return <RouteErrorBoundaryInner pathname={pathname}>{children}</RouteErrorBoundaryInner>
}

export function App() {
  return (
    <AuthProvider>
      <Router>
        <VersionGuard />
        <LocaleProvider>
          <Layout>
            <RouteErrorBoundary>
              <Routes>
                {LOCALES.map((l) => localeRoutes(l.prefix))}
                {/* Localized country landing pages (the money pages) — one per locale gateway. */}
                {Object.entries(GATEWAYS).map(([code, g]) => (
                  <Route
                    key={g.slug}
                    path={`${localeMeta(code).prefix}/${g.slug}`}
                    element={<CountryLandingPage />}
                  />
                ))}
                {/* Chinese forwarder-partner page stays root-only. */}
                <Route path="/cn" element={<CnPartnersPage />} />
                {/* The customer's own lanes. Root-only and private: the Navo24 workspace opens it for owners. */}
                <Route path="/dashboard" element={<DashboardPage />} />
                {/* How to connect an AI assistant. Registries and the MCP server's hints link here. */}
                <Route path="/mcp" element={<McpPage />} />
                <Route path="*" element={<HomePage />} />
              </Routes>
            </RouteErrorBoundary>
          </Layout>
        </LocaleProvider>
      </Router>
    </AuthProvider>
  )
}
